import React from 'react';
import { formatDate, formatDateTime } from '../lib/datetime';

// Shared presentational building blocks + style tokens for admin pages.
// Keeps every admin page visually consistent and handles the empty / loading /
// error states so a null/empty API response never turns into a broken screen.

export function PageHeader({ title, subtitle, children }) {
  return (
    <div style={ui.pageHeader}>
      <div>
        <h1 style={ui.title}>{title}</h1>
        {subtitle && <p style={ui.subtitle}>{subtitle}</p>}
      </div>
      {children && <div style={ui.headerActions}>{children}</div>}
    </div>
  );
}

export function Loading({ text = 'Yuklanmoqda...' }) {
  return <div style={ui.centerMsg}>{text}</div>;
}

export function ErrorBanner({ children }) {
  if (!children) return null;
  return <div style={ui.errorBanner}>{children}</div>;
}

export function Empty({ icon = '📭', title = "Hali ma'lumot yo'q", hint }) {
  return (
    <div style={ui.empty}>
      <div style={ui.emptyIcon}>{icon}</div>
      <p style={ui.emptyText}>{title}</p>
      {hint && <p style={ui.emptyHint}>{hint}</p>}
    </div>
  );
}

export function Badge({ children, tone = 'blue' }) {
  return <span style={{ ...ui.badge, ...(TONES[tone] || TONES.blue) }}>{children}</span>;
}

export function Modal({ title, onClose, children, maxWidth = '520px' }) {
  return (
    <div style={ui.overlay} onClick={onClose}>
      <div style={{ ...ui.modalCard, maxWidth }} onClick={(e) => e.stopPropagation()}>
        {title && <h2 style={ui.modalTitle}>{title}</h2>}
        {children}
      </div>
    </div>
  );
}

// Formats a backend ISO timestamp for display. Timezone-safe (no ±5h drift)
// and never shows seconds — delegates to the shared datetime helper.
export function fmtDate(iso, withTime = false) {
  const r = withTime ? formatDateTime(iso) : formatDate(iso);
  return r || '—';
}

// Extracts an array payload from the various shapes the backend uses
// ({items:[]}, {data:[]}, or a bare array). Never throws on empty/null.
export function asList(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.items)) return data.items;
  if (data && Array.isArray(data.data)) return data.data;
  return [];
}

const TONES = {
  blue: { background: 'var(--info-tint)', color: 'var(--info)', border: '1px solid #bfdbfe' },
  green: { background: 'var(--success-tint)', color: 'var(--success)', border: '1px solid #bbf7d0' },
  red: { background: 'var(--danger-tint)', color: 'var(--danger)', border: '1px solid #fecaca' },
  amber: { background: '#fffbeb', color: '#b45309', border: '1px solid #fcd34d' },
  gray: { background: 'var(--surface-alt)', color: 'var(--text-muted)', border: '1px solid #e5e7eb' },
  purple: { background: '#f5f3ff', color: '#6d28d9', border: '1px solid #ddd6fe' },
};

export const ui = {
  pageHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: '24px',
    gap: '16px',
    flexWrap: 'wrap',
  },
  title: { margin: '0 0 4px', fontSize: '22px', fontWeight: '700', color: 'var(--text)' },
  subtitle: { margin: 0, fontSize: '14px', color: 'var(--text-muted)' },
  headerActions: { display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' },
  addBtn: {
    padding: '10px 20px',
    background: 'var(--brand)',
    color: 'var(--on-accent)',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  errorBanner: {
    background: 'var(--danger-tint)',
    border: '1px solid #fca5a5',
    borderRadius: '8px',
    padding: '12px 16px',
    color: 'var(--danger)',
    fontSize: '14px',
    marginBottom: '16px',
  },
  infoBanner: {
    background: '#fffbeb',
    border: '1px solid #fcd34d',
    borderRadius: '8px',
    padding: '12px 16px',
    color: '#92400e',
    fontSize: '14px',
    marginBottom: '16px',
    lineHeight: 1.5,
  },
  centerMsg: { textAlign: 'center', padding: '60px', color: 'var(--text-faint)', fontSize: '15px' },
  empty: {
    textAlign: 'center',
    padding: '64px 24px',
    background: 'var(--surface)',
    borderRadius: '12px',
    border: '1px dashed #d1d5db',
  },
  emptyIcon: { fontSize: '40px', marginBottom: '12px' },
  emptyText: { margin: '0 0 6px', fontSize: '16px', fontWeight: '600', color: 'var(--text-secondary)' },
  emptyHint: { margin: 0, fontSize: '14px', color: 'var(--text-faint)' },
  tableWrap: {
    background: 'var(--surface)',
    borderRadius: '12px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    overflowY: 'hidden',
    overflowX: 'auto',
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontWeight: '600',
    color: 'var(--text-muted)',
    background: 'var(--bg)',
    borderBottom: '1px solid #e5e7eb',
    fontSize: '13px',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    whiteSpace: 'nowrap',
  },
  tr: { borderBottom: '1px solid #f3f4f6' },
  td: { padding: '14px 16px', verticalAlign: 'middle', fontSize: '14px', color: 'var(--text-secondary)' },
  badge: {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
  },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap' },
  btnSm: {
    padding: '5px 12px',
    borderRadius: '6px',
    fontSize: '13px',
    cursor: 'pointer',
    fontWeight: '500',
    border: '1px solid transparent',
  },
  btnBlue: { background: 'var(--info-tint)', color: 'var(--info)', border: '1px solid #bfdbfe' },
  btnGreen: { background: 'var(--success-tint)', color: 'var(--success)', border: '1px solid #bbf7d0' },
  btnRed: { background: 'var(--danger-tint)', color: 'var(--danger)', border: '1px solid #fecaca' },
  btnGray: { background: 'var(--surface-alt)', color: 'var(--text-secondary)', border: '1px solid #e5e7eb' },
  searchInput: {
    padding: '9px 14px',
    border: '1.5px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    color: 'var(--text)',
    outline: 'none',
    minWidth: '240px',
    background: 'var(--surface)',
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 200,
    padding: '16px',
  },
  modalCard: {
    background: 'var(--surface)',
    borderRadius: '14px',
    padding: '28px 28px 24px',
    width: '100%',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  modalTitle: { margin: '0 0 20px', fontSize: '18px', fontWeight: '700', color: 'var(--text)' },
  field: { marginBottom: '16px' },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontSize: '13px',
    fontWeight: '600',
    color: 'var(--text-secondary)',
  },
  req: { color: 'var(--brand)' },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1.5px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    color: 'var(--text)',
    outline: 'none',
    boxSizing: 'border-box',
    background: 'var(--surface)',
    fontFamily: 'inherit',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    border: '1.5px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    color: 'var(--text)',
    outline: 'none',
    boxSizing: 'border-box',
    background: 'var(--surface)',
    resize: 'vertical',
    fontFamily: 'inherit',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    border: '1.5px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    color: 'var(--text)',
    outline: 'none',
    boxSizing: 'border-box',
    background: 'var(--surface)',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  formError: {
    background: 'var(--danger-tint)',
    border: '1px solid #fca5a5',
    borderRadius: '8px',
    padding: '10px 12px',
    color: 'var(--danger)',
    fontSize: '13px',
    marginBottom: '12px',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginTop: '8px',
  },
  cancelBtn: {
    padding: '9px 18px',
    background: 'var(--surface-alt)',
    color: 'var(--text-secondary)',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: 'pointer',
    fontWeight: '500',
  },
  saveBtn: {
    padding: '9px 22px',
    background: 'var(--brand)',
    color: 'var(--on-accent)',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: 'pointer',
    fontWeight: '600',
  },
  btnDisabled: { opacity: 0.65, cursor: 'not-allowed' },
  // pagination
  pager: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    marginTop: '16px',
  },
  pagerBtn: {
    padding: '7px 14px',
    background: 'var(--surface)',
    color: 'var(--text-secondary)',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: 'pointer',
    fontWeight: '500',
  },
  pagerInfo: { fontSize: '14px', color: 'var(--text-muted)' },
  // stat cards
  statGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: '14px',
    marginBottom: '24px',
  },
  statCard: {
    background: 'var(--surface)',
    borderRadius: '12px',
    padding: '18px 20px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    border: '1px solid #eef0f3',
  },
  statValue: { fontSize: '26px', fontWeight: '700', color: 'var(--text)', lineHeight: 1.1 },
  statLabel: { fontSize: '13px', color: 'var(--text-muted)', marginTop: '6px' },
};

export function StatCard({ label, value, accent = '#1a1a2e' }) {
  return (
    <div style={ui.statCard}>
      <div style={{ ...ui.statValue, color: accent }}>{value}</div>
      <div style={ui.statLabel}>{label}</div>
    </div>
  );
}
